// Google Translate (and similar extensions) replace React-owned text nodes with
// <font> wrappers, so React's later insertBefore/removeChild calls reference a
// node that is no longer a direct child and throw NotFoundError
// (facebook/react#11538). Translation stays allowed; these patches retarget the
// call at the wrapper instead.
let installed = false;

function directChildContaining(parent: Node, node: Node): Node | null {
  let current: Node | null = node;
  while (current !== null && current.parentNode !== parent) {
    current = current.parentNode;
  }
  return current;
}

export function installTranslationDomGuard(): void {
  if (installed) return;
  installed = true;

  // eslint-disable-next-line @typescript-eslint/unbound-method -- always invoked via .call with the receiver
  const originalInsertBefore = Node.prototype.insertBefore;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- always invoked via .call with the receiver
  const originalRemoveChild = Node.prototype.removeChild;

  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null
  ): T {
    if (child === null || child.parentNode === this) {
      return originalInsertBefore.call<Node, [T, Node | null], T>(
        this,
        node,
        child
      );
    }
    // A detached reference leaves no position to recover; appending keeps the
    // node in the tree so React's later updates to it still apply.
    return originalInsertBefore.call<Node, [T, Node | null], T>(
      this,
      node,
      directChildContaining(this, child)
    );
  };

  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode === this) {
      return originalRemoveChild.call<Node, [T], T>(this, child);
    }
    const wrapper = directChildContaining(this, child);
    if (wrapper !== null) originalRemoveChild.call(this, wrapper);
    return child;
  };
}
